import { useCallback, useMemo } from 'react';
import { getOrderingParam } from 'utility/SortingHelpers';
import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { useActivityFieldsQuery } from '__queries/models/activities-ts';
import { snakeToCamelCaseKeys } from '__services/helpers';

type Criteria = {
  search: string | undefined;
  ordering: string;
  pageSize: number;
  completed: boolean;
  page: number;
};

type FetchActivitiesProps = {
  activityObjectId: string;
  searchTerm: string | null;
  getQueryKey: any;
  getInvalidateQueryKey: (activityObjectId: string) => string[];
  fetchActivitiesFunc: any;
  sortOrder: any;
  pageSize: number | null;
  page: number | null;
  debouncedTerm: string | null;
  notesValue: {
    setCurrentId: (value: any) => void;
    setOriginalNote: (value: any) => void;
    isDirty: boolean;
    currentId: any;
    reset: () => void;
    setIsDirty: (value: ((prevState: boolean) => boolean) | boolean) => void;
    originalNote: any;
  };
  isPageConfigLoading: boolean;
  savedColumns: any;
};

export const useFetchActivities = ({
  activityObjectId,
  notesValue,
  sortOrder,
  pageSize,
  page,
  debouncedTerm,
  getQueryKey,
  getInvalidateQueryKey,
  fetchActivitiesFunc,
  isPageConfigLoading,
  searchTerm,
  savedColumns,
}: FetchActivitiesProps) => {
  const fetchActivities = useCallback(
    async (
      params: Criteria
    ): Promise<{
      results?: any;
      next?: any;
      count?: number;
      error?: any;
    }> => {
      try {
        const response = await fetchActivitiesFunc({
          params: { ...params, activityObjectId },
        });
        notesValue.reset();
        return response;
      } catch (error: any) {
        return {
          error: error?.response,
        };
      }
    },
    [notesValue, fetchActivitiesFunc, activityObjectId]
  );

  const criteria: Criteria = useMemo(() => {
    return {
      completed: false,
      ordering: getOrderingParam(sortOrder ?? {}),
      pageSize: pageSize ?? 0,
      page: page ?? 0,
      search: debouncedTerm ?? undefined,
      activityTableSearch: true,
      fields: savedColumns,
    };
  }, [debouncedTerm, sortOrder, pageSize, page, savedColumns]);

  const queryKey = useMemo(() => {
    return getQueryKey(activityObjectId, criteria);
  }, [criteria, activityObjectId, getQueryKey]);

  const invalidateQueryKey = useMemo(() => {
    return getInvalidateQueryKey(activityObjectId);
  }, [activityObjectId, getInvalidateQueryKey]);

  const query: UseQueryResult<
    { results: any[]; next: any; count: number },
    any
  > = useQuery({
    queryKey,
    queryFn: async () => {
      const { results, next, count, error } = await fetchActivities(criteria);
      if (error) {
        throw error;
      }
      return { results, next, count };
    },
    retry: false,
    throwOnError: false,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    enabled:
      Boolean(queryKey) &&
      !isPageConfigLoading &&
      Boolean(pageSize) &&
      Boolean(page) &&
      Boolean(sortOrder) &&
      searchTerm !== null,
  });

  const { data: tableData } = query;

  const activities = useMemo(() => {
    return tableData?.results;
  }, [tableData]);

  const activityCount = useMemo(() => tableData?.count ?? null, [tableData]);

  const { data: fields = [], isLoading: isLoadingFields } =
    useActivityFieldsQuery(activityObjectId as string, {
      select: (fields) => {
        const sorted = fields.sort((a, b) => {
          if (a.is_hidden && !b.is_hidden) {
            return 1;
          }
          if (b.is_hidden && !a.is_hidden) {
            return -1;
          }
          return a.order! - b.order!;
        });
        return snakeToCamelCaseKeys(
          sorted.filter(({ field_type }) => field_type !== 'wysiwyg')
        );
      },
    });

  return {
    fields,
    isLoadingFields,
    query,
    queryKey,
    invalidateQueryKey,
    criteria,
    activities,
    activityCount,
  };
};
